
import { Component, Vue } from 'vue-property-decorator';
import { ContentLoader } from 'vue-content-loader';
import { AccountInfo } from '@airgap/beacon-sdk';
import { getTokenInfoContract } from '@/lib/requests-helper';
import { Token, TradeFromTzstats } from '@/lib/types-helper';
import { Module1Module } from '@/store/modules/module1';

@Component({
  components: {
    ContentLoader
  }
})
export default class Accept extends Vue {
  mutez_amount1 = 0;
  mutez_amount2 = 0;
  tokens1: Token[] = [];
  tokens2: Token[] = [];
  searchedTradeId: number | null = null;
  status = 'None';

  showModal = false;
  allowModalToBeClosed = false;
  modalTitle = 'Please Wait';
  modalParagraph = 'Transaction pending...'

  get account () : AccountInfo | undefined | null {
    return Module1Module.account;
  }

  get allContractTradeData () : TradeFromTzstats[] {
    return Module1Module.allContractTradeData;
  }

  get myContractTradeData () : TradeFromTzstats[] {
    return Module1Module.myContractTradeData;
  }

  get isDev () : boolean {
    return Module1Module.isDev;
  }

  async mounted () : Promise<void> {
    // for deeplinking to a trade with logged-out users
    const searchParams = new URLSearchParams(window.location.search);
    const tradeid = searchParams.get('trade');
    if (tradeid) {
      this.searchedTradeId = parseInt(tradeid);
      await this.inspectTrade();
    }
  }

  updateTradeId (event: Event) : void {
    this.searchedTradeId = parseInt((event.target as HTMLInputElement).value);
  }

  tezosToMutez (tezos: number) : number {
    return tezos * 1000000;
  }

  getTotalTokensFrom (tokens: any[]) : number {
    let total = 0;
    tokens.forEach(token => {
      total += token.amount;
    });
    return total;
  }

  possiblyCloseModal () : void {
    if (this.allowModalToBeClosed) {
      this.showModal = false;
      this.allowModalToBeClosed = false;
    }
  }

  getImageURLFromDisplayURI (uri: string) : string {
    let newUri = uri;

    if (uri.includes('ipfs://')) {
      newUri = uri.replace('ipfs://', 'https://ipfs.io/ipfs/');
    }

    if (uri.includes('ar://')) {
      newUri = uri.replace('ar://', 'https://arweave.new/');
    }

    return newUri;
  }

  async login () : Promise<void> {
    try {
      this.showModal = true;
      this.modalTitle = 'Logging In';
      this.modalParagraph = 'Please wait...';

      await Module1Module.Login();

      this.showModal = false;
    } catch (error: any) {
      this.showModal = true;
      this.modalTitle = '<span class="text-red-600">Error: ' + error.title + '</span>';
      this.modalParagraph = error.description;

      if (!error.title && !error.description) {
        this.modalTitle = '<span class="text-red-600">Error</span>';
        this.modalParagraph = 'If this was unexpected please contact support';
      }

      this.allowModalToBeClosed = true;
      console.warn(error);
    }
  }

  async inspectTrade () : Promise<void> {
    try {
      await Module1Module.FetchAllContractTradeData();

      this.tokens1 = [];
      this.tokens2 = [];
      this.mutez_amount1 = 0;
      this.mutez_amount2 = 0;

      if (this.searchedTradeId !== undefined && this.searchedTradeId !== null && this.searchedTradeId >= 0) {
        const theTrade = Module1Module.allContractTradeData[this.searchedTradeId];

        if (!theTrade) {
          this.showModal = true;
          this.allowModalToBeClosed = true;
          this.modalTitle = 'Trade DNE';
          this.modalParagraph = 'There is no trade with ID #' + this.searchedTradeId;
          return;
        }

        this.mutez_amount1 = theTrade.value.proposal.mutez_amount1 / 1000000;
        this.mutez_amount2 = theTrade.value.proposal.mutez_amount2 / 1000000;
        this.status = theTrade.value.executed ? 'Closed' : 'Open';

        for (let i = 0; i < theTrade.value.proposal.tokens1.length; i++) {
          const token = theTrade.value.proposal.tokens1[i];

          const info = await getTokenInfoContract(token.id as string, token.fa2 as string);
          if (!info) {
            this.showModal = true;
            this.allowModalToBeClosed = true;
            this.modalTitle = 'Malformed Token in Trade';
            this.modalParagraph = 'Could not get Objkt data for token #' + token.id + 'on contract ' + token.fa2;
            return;
          }

          this.tokens1.push({
            id: parseInt(token.id.toString()),
            amount: parseInt(token.amount.toString()),
            fa2: token.fa2,
            name: info.name,
            image: this.getImageURLFromDisplayURI(info.display_uri),
            royalty_addresses: token.royalty_addresses
          });
        }

        for (let i = 0; i < theTrade.value.proposal.tokens2.length; i++) {
          const token = theTrade.value.proposal.tokens2[i];

          const info = await getTokenInfoContract(token.id as string, token.fa2 as string);
          if (!info) {
            this.showModal = true;
            this.allowModalToBeClosed = true;
            this.modalTitle = 'Malformed Token in Trade';
            this.modalParagraph = 'Could not get Objkt data for token #' + token.id + ' on contract ' + token.fa2;
            return;
          }

          this.tokens2.push({
            id: parseInt(token.id.toString()),
            amount: parseInt(token.amount.toString()),
            fa2: token.fa2,
            name: info.name,
            image: this.getImageURLFromDisplayURI(info.display_uri),
            royalty_addresses: token.royalty_addresses
          });
        }

        this.showModal = false;
      } else {
        this.modalTitle = '<span class="text-red-600">No Trade ID</span>';
        this.modalParagraph = 'Please input a trade ID to inspect';

        this.showModal = true;
        this.allowModalToBeClosed = true;
      }
    } catch (error) {
      console.warn(error);
      this.showModal = true;
      this.allowModalToBeClosed = true;
      this.modalTitle = 'Error';
      this.modalParagraph = 'There was an issue with that search';
    }
  }

  async acceptTrade () : Promise<void> {
    this.allowModalToBeClosed = false;

    try {
      if (!Module1Module.account) {
        await this.login();
      }

      const trade_id = (this.$refs.acceptTradeID as HTMLInputElement).value;
      if (!trade_id) {
        const err = new Error('Blank trade ID');
        Object.assign(err, {
          title: 'Error: Blank',
          description: 'Blank trade ID. Please insert a trade ID.'
        });
        throw err;
      }

      // make sure contract trade data is up to date
      await Module1Module.FetchAllContractTradeData();

      const theTrade = Module1Module.allContractTradeData.find((datum: TradeFromTzstats) => datum.key === '' + trade_id);

      if (!theTrade) {
        const err = new Error('Trade DNE');
        Object.assign(err, {
          title: 'Does Not Exist',
          description: 'There is no trade with that ID. If this seems like a mistake, let us know.'
        });
        throw err;
      }

      if (Module1Module.xtznftswapContract && Module1Module.account) {
        const batch = await Module1Module.tezosToolkitInstance.wallet.batch();

        // for all the tokens on the accepting side
        for (let i = 0; i < theTrade.value.proposal.tokens2.length; i++) {
          const token = theTrade.value.proposal.tokens2[i];
          const fa2Contract = await Module1Module
            .tezosToolkitInstance.wallet.at(token.fa2);

          // give our contract operator rights
          batch.withContractCall(
            fa2Contract.methods.update_operators([{
              add_operator: {
                owner: Module1Module.account.address,
                operator: Module1Module.xtznftswapContract.address,
                token_id: token.id
              }
            }])
          );
        }

        // then accept the trade
        batch.withTransfer({
          ...Module1Module.xtznftswapContract.methodsObject
            .accept_trade(theTrade.key)
            .toTransferParams({
              amount: theTrade.value.proposal.mutez_amount2 / 1000000 // in tez
            })
        });

        this.showModal = true;
        this.modalTitle = 'Please Wait';
        this.modalParagraph = 'Waiting for you to accept the transaction in your wallet...';
        const batchOp = await batch.send();

        this.modalTitle = 'Please Wait';
        this.modalParagraph = 'Confirming trade on the blockchain...';
        // alters the batchOp object to have operationResults() when successful
        await batchOp.confirmation();
        const hash = batchOp.opHash;

        this.modalTitle = 'Success!';
        this.modalParagraph = 'You accepted trade ID #' + trade_id + `! You can view the <a href="https://tzkt.io/${hash}" target="_blank">details of this trade on tzkt.io</a> anytime.`;
        this.allowModalToBeClosed = true;
      }
    } catch (error: any) {
      this.modalTitle = '<span class="text-red-600">Error: ' + error.title + '</span>';
      this.modalParagraph = error.description;

      if (!error.title && !error.description) {
        this.modalTitle = '<span class="text-red-600">Error</span>';
        this.modalParagraph = 'If this was unexpected please contact support';
      }

      this.showModal = true;
      this.allowModalToBeClosed = true;
    }
  }
}
